/**
 * TipTap keyboard shortcuts aligned with Slack desktop message formatting.
 * @see https://slack.com/help/articles/201374536-Slack-keyboard-shortcuts
 *
 * Underline (⌘/Ctrl+U on Mac Slack) is intentionally a no-op: we persist
 * markdown without HTML and cannot round-trip underline.
 *
 * Enter stays multiline in Settings style notes (no send). Future Slack reply
 * mode may opt into submit-on-Enter separately.
 */

import { Extension, type Editor } from '@tiptap/core';

/** Persistable http(s) only — shared with RichTextComposer link UI. */
export function isSafeHttpUrl(href: string): boolean {
  try {
    const parsed = new URL(href);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function promptForLink(editor: Editor): boolean {
  const previous = editor.getAttributes('link').href;
  const previousHref = typeof previous === 'string' ? previous : '';
  const next = window.prompt('URL del enlace', previousHref || 'https://');
  if (next === null) return true;
  const trimmed = next.trim();
  if (!trimmed) {
    return editor.chain().focus().extendMarkRange('link').unsetLink().run();
  }
  if (!isSafeHttpUrl(trimmed)) return true;
  return editor.chain().focus().extendMarkRange('link').setLink({ href: trimmed }).run();
}

export const SlackKeyboardShortcuts = Extension.create({
  name: 'slackKeyboardShortcuts',

  addKeyboardShortcuts() {
    return {
      // Slack: Bold / Italic (also StarterKit defaults; keep explicit for parity docs)
      'Mod-b': () => this.editor.commands.toggleBold(),
      'Mod-i': () => this.editor.commands.toggleItalic(),

      // Slack Mac lists Underline as ⌘U — not persistable in our markdown store
      'Mod-u': () => true,

      // Slack: Strikethrough
      'Mod-Shift-x': () => this.editor.commands.toggleStrike(),

      // Slack: Hyperlink (not underline)
      'Mod-Shift-u': () => promptForLink(this.editor),

      // Slack: Inline code / code block
      'Mod-Shift-c': () => this.editor.commands.toggleCode(),
      'Mod-Alt-Shift-c': () => this.editor.commands.toggleCodeBlock(),

      // Slack: Lists + quote. Bind digit keys and US Shift glyphs (* & () —
      // w3c-keyname reports event.key, which is the shifted character.
      'Mod-Shift-8': () => this.editor.commands.toggleBulletList(),
      'Mod-Shift-*': () => this.editor.commands.toggleBulletList(),
      'Mod-Shift-7': () => this.editor.commands.toggleOrderedList(),
      'Mod-Shift-&': () => this.editor.commands.toggleOrderedList(),
      'Mod-Shift-9': () => this.editor.commands.toggleBlockquote(),
      'Mod-Shift-(': () => this.editor.commands.toggleBlockquote(),
    };
  },
});
